/**
 * Botones de una plantilla: respuesta rápida, link o teléfono, según el tipo.
 *
 * - Respuesta rápida (`twilio/quick-reply`): hasta 10, el de baja incluido, título ≤ 20.
 * - Link o teléfono (`twilio/call-to-action`): hasta 2 links y 1 teléfono, 3 en total, título ≤ 20.
 * - Tarjeta (`whatsapp/card`, D-A): respuestas rápidas con título ≤ 25 y hasta 2 links (≤ 20). Sin
 *   teléfono en el MVP: el validador lo rechaza, así que acá ni se ofrece.
 *
 * El botón de baja (id `BAJA`) lleva el texto de Configuración y no se edita desde acá. En una
 * promoción se precarga (`aplicarReglasDeCategoria`) y, desde el 16/10/2026 (pedido de Alan), se puede
 * sacar; si falta, se ofrece volver a ponerlo, último. Sin él, el validador avisa y no frena.
 *
 * El id de cada respuesta rápida se arma solo a partir del título (`idBoton`): es lo que llega
 * en `ButtonPayload` cuando la persona lo toca. Se puede cambiar en «Avanzado».
 *
 * Un link puede «contar quién lo toca» (`logic/clics`, pedido de Llama Leads del 26/10/2026): pasa por un link de
 * Deenex que anota el clic y lleva al destino. Lo arma `onClic` del editor, que conoce la plantilla entera.
 */

import { useId, useState } from "react";
import { AlertTriangle, ExternalLink, Lock, PhoneCall, Plus, Reply, Trash2 } from "lucide-react";
import type { BotonPlantilla, Categoria, TipoPlantilla } from "../types";
import { ID_BOTON_BAJA, idBoton } from "../logic/plantilla";
import { numeroDeClic } from "../logic/clics";

interface Props {
  tipo: TipoPlantilla;
  botones: BotonPlantilla[];
  onChange: (botones: BotonPlantilla[]) => void;
  /** texto del botón de baja de Configuración (sólo para mostrarlo) */
  botonBaja?: string;
  categoria: Categoria;
  /** el comienzo del link de Deenex que cuenta los clics; sin él (sin link público) no se ofrece */
  prefijoClics?: string | null;
  /** prende o apaga «Contar quién lo toca» en el botón `i` */
  onClic?: (i: number, activar: boolean) => void;
}

/** Límites por tipo, iguales a los del validador del backend (LIMITES de plantilla.util.ts). */
const MAX_BOTONES = 10;
const MAX_LINKS = 2;
const MAX_TELEFONOS = 1;
const MAX_CTA = 3;
const TITULO = 20;
const TITULO_RESPUESTA_TARJETA = 25;

export function maxTitulo(tipo: TipoPlantilla, b: Pick<BotonPlantilla, "tipo">): number {
  return tipo === "whatsapp/card" && b.tipo === "QUICK_REPLY" ? TITULO_RESPUESTA_TARJETA : TITULO;
}

export function EditorBotones({ tipo, botones, onChange, botonBaja, categoria, prefijoClics, onClic }: Props) {
  const lista = botones || [];
  const esRespuestas = tipo === "twilio/quick-reply";
  const esCta = tipo === "twilio/call-to-action";
  const esTarjeta = tipo === "whatsapp/card";

  if (!esRespuestas && !esCta && !esTarjeta) {
    return <p className="text-xs text-slate-400">Este tipo de plantilla no lleva botones.</p>;
  }

  const nLinks = lista.filter((b) => b.tipo === "URL").length;
  const nTel = lista.filter((b) => b.tipo === "PHONE_NUMBER").length;
  const total = lista.length;

  const puedeRespuesta = (esRespuestas || esTarjeta) && total < MAX_BOTONES;
  const puedeLink = (esCta && total < MAX_CTA && nLinks < MAX_LINKS) || (esTarjeta && total < MAX_BOTONES && nLinks < MAX_LINKS);
  const puedeTel = esCta && total < MAX_CTA && nTel < MAX_TELEFONOS;

  const idsMenos = (i: number) => lista.filter((_, j) => j !== i).map((b) => b.id || "").filter(Boolean);

  /** Los botones nuevos van antes del de baja: el de baja queda siempre último. */
  const agregar = (b: BotonPlantilla) => {
    const iBaja = lista.findIndex((x) => x.id === ID_BOTON_BAJA);
    const nueva = iBaja === -1 ? [...lista, b] : [...lista.slice(0, iBaja), b, ...lista.slice(iBaja)];
    onChange(nueva);
  };

  const cambiar = (i: number, parcial: Partial<BotonPlantilla>) => {
    const actual = lista[i];
    const siguiente: BotonPlantilla = { ...actual, ...parcial };
    // El id sigue al título mientras sea el automático; si el operador lo cambió a mano, se respeta.
    if (actual.tipo === "QUICK_REPLY" && parcial.titulo !== undefined) {
      const otros = idsMenos(i);
      const eraAutomatico = !actual.id || actual.id === idBoton(actual.titulo, otros);
      if (eraAutomatico) siguiente.id = idBoton(parcial.titulo, otros);
    }
    onChange(lista.map((b, j) => (j === i ? siguiente : b)));
  };

  const sacar = (i: number) => onChange(lista.filter((_, j) => j !== i));

  return (
    <div className="space-y-3">
      {lista.length === 0 && (
        <p className="text-xs text-slate-400">
          {esCta ? "Agregá al menos un link o un teléfono." : esTarjeta ? "La tarjeta puede ir sin botones, o con respuestas y hasta 2 links." : "Agregá al menos un botón."}
        </p>
      )}

      <ul className="space-y-2">
        {lista.map((b, i) =>
          b.id === ID_BOTON_BAJA ? (
            <li key={`baja-${i}`} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Lock size={14} className="text-slate-400 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700 truncate">{b.titulo || botonBaja}</span>
                <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Baja</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <p className="text-[11px] text-slate-500">Quien lo toca queda dado de baja. El texto se cambia en Configuración.</p>
                <button type="button" onClick={() => sacar(i)} aria-label="Sacar el botón de baja"
                  className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-red-600">
                  <Trash2 size={12} /> Sacar
                </button>
              </div>
            </li>
          ) : (
            <FilaBoton
              key={`${b.tipo}-${i}`}
              tipo={tipo}
              boton={b}
              onCambiar={(p) => cambiar(i, p)}
              onSacar={() => sacar(i)}
              onClic={onClic && prefijoClics ? (activar) => onClic(i, activar) : undefined}
            />
          ),
        )}
      </ul>

      <div className="flex flex-wrap gap-2">
        {categoria === "MARKETING" && (esRespuestas || esTarjeta) && !lista.some((x) => x.id === ID_BOTON_BAJA) && (
          <BotonAgregar disabled={total >= MAX_BOTONES} onClick={() => onChange([...lista, { tipo: "QUICK_REPLY", id: ID_BOTON_BAJA, titulo: botonBaja || "No quiero más" }])}
            icono={<Reply size={13} />} texto={`Agregar el botón de baja («${botonBaja || "No quiero más"}»)`} />
        )}
        {(esRespuestas || esTarjeta) && (
          <BotonAgregar disabled={!puedeRespuesta} onClick={() => agregar({ tipo: "QUICK_REPLY", titulo: "", id: idBoton("", lista.map((x) => x.id || "")) })}
            icono={<Reply size={13} />} texto="Agregar respuesta" />
        )}
        {(esCta || esTarjeta) && (
          <BotonAgregar disabled={!puedeLink} onClick={() => agregar({ tipo: "URL", titulo: "", url: "" })} icono={<ExternalLink size={13} />} texto="Agregar link" />
        )}
        {esCta && (
          <BotonAgregar disabled={!puedeTel} onClick={() => agregar({ tipo: "PHONE_NUMBER", titulo: "", telefono: "" })} icono={<PhoneCall size={13} />} texto="Agregar teléfono" />
        )}
      </div>

      <p className="text-[11px] text-slate-400">
        {esCta
          ? "Hasta 2 links y 1 teléfono (3 botones en total)."
          : esTarjeta
            ? `Hasta ${MAX_BOTONES} botones: respuestas de hasta ${TITULO_RESPUESTA_TARJETA} caracteres y hasta 2 links de ${TITULO}.`
            : `Hasta ${MAX_BOTONES} botones${categoria === "MARKETING" ? ", contando el de baja" : ""}.`}
      </p>

      {total >= 4 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-700">
          <AlertTriangle size={13} className="flex-shrink-0 mt-px" />
          Con 4 o más botones no se ve en WhatsApp de escritorio.
        </p>
      )}
    </div>
  );
}

function BotonAgregar({ disabled, onClick, icono, texto }: { disabled: boolean; onClick: () => void; icono: JSX.Element; texto: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <Plus size={13} />
      {icono}
      {texto}
    </button>
  );
}

function FilaBoton({ tipo, boton, onCambiar, onSacar, onClic }: {
  tipo: TipoPlantilla; boton: BotonPlantilla; onCambiar: (p: Partial<BotonPlantilla>) => void; onSacar: () => void;
  onClic?: (activar: boolean) => void;
}) {
  const [avanzado, setAvanzado] = useState(false);
  const idTitulo = useId();
  const idExtra = useId();
  const max = maxTitulo(tipo, boton);
  const largo = (boton.titulo || "").length;
  const etiqueta = boton.tipo === "QUICK_REPLY" ? "Respuesta" : boton.tipo === "URL" ? "Link" : "Teléfono";
  const icono = boton.tipo === "QUICK_REPLY" ? <Reply size={13} /> : boton.tipo === "URL" ? <ExternalLink size={13} /> : <PhoneCall size={13} />;
  // con el link de Deenex que cuenta los clics, lo que se escribe es el destino
  const cuentaClics = boton.tipo === "URL" && numeroDeClic(boton.url) !== null;
  const conVariable = !cuentaClics && /\{\{/.test(boton.url || "");

  return (
    <li className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 space-y-2">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{icono}{etiqueta}</span>
        <button type="button" onClick={onSacar} aria-label={`Sacar el botón ${boton.titulo || etiqueta}`}
          className="ml-auto w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50">
          <Trash2 size={14} />
        </button>
      </div>

      <div>
        <label htmlFor={idTitulo} className="text-xs font-semibold text-slate-600">Texto del botón</label>
        <div className="relative mt-1">
          <input
            id={idTitulo}
            value={boton.titulo}
            onChange={(e) => onCambiar({ titulo: e.target.value })}
            className={`w-full rounded-lg border px-3 py-1.5 pr-14 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${largo > max ? "border-red-300" : "border-slate-200"}`}
          />
          <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] tabular-nums ${largo > max ? "text-red-600 font-semibold" : "text-slate-400"}`}>
            {largo}/{max}
          </span>
        </div>
      </div>

      {boton.tipo === "URL" && (
        <div>
          <label htmlFor={idExtra} className="text-xs font-semibold text-slate-600">{cuentaClics ? "A dónde lleva" : "Link"}</label>
          <input
            id={idExtra}
            value={cuentaClics ? boton.destino || "" : boton.url || ""}
            onChange={(e) => onCambiar(cuentaClics ? { destino: e.target.value.trim() } : { url: e.target.value.trim() })}
            placeholder={cuentaClics ? "https://tu.web/pedidos" : "https://tu.web/promo/{{1}}"}
            inputMode="url"
            spellCheck={false}
            className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            {cuentaClics ? "Un link https, sin variables. Nunca un link de WhatsApp." : "Si lleva una variable, va al final y después de una barra. Nunca un link de WhatsApp."}
          </p>
          {onClic && (
            <label className="mt-2 flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5" checked={cuentaClics} disabled={conVariable} onChange={(e) => onClic(e.target.checked)} />
              <span>
                Contar quién lo toca
                <span className="block text-[11px] text-slate-500">
                  {cuentaClics
                    ? `Meta aprueba el link de Deenex (${boton.url}): cada persona lo abre con su código, queda anotado y la lleva al link de arriba. Se ve en las estadísticas de la campaña.`
                    : conVariable
                      ? "Con una variable en el link no se pueden contar los clics."
                      : "El botón pasa por un link de Deenex que anota quién lo toca y la lleva a tu link."}
                </span>
              </span>
            </label>
          )}
        </div>
      )}

      {boton.tipo === "PHONE_NUMBER" && (
        <div>
          <label htmlFor={idExtra} className="text-xs font-semibold text-slate-600">Teléfono</label>
          <input
            id={idExtra}
            value={boton.telefono || ""}
            onChange={(e) => onCambiar({ telefono: e.target.value.trim() })}
            placeholder="+5491155443322"
            inputMode="tel"
            className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
      )}

      {boton.tipo === "QUICK_REPLY" && (
        <div>
          <button type="button" onClick={() => setAvanzado((v) => !v)} aria-expanded={avanzado} className="text-[11px] font-medium text-slate-500 hover:text-slate-700">
            {avanzado ? "Ocultar avanzado" : "Avanzado"}
          </button>
          {avanzado && (
            <div className="mt-1.5">
              <label htmlFor={idExtra} className="text-xs font-semibold text-slate-600">Código que llega cuando lo tocan</label>
              <input
                id={idExtra}
                value={boton.id || ""}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "").slice(0, 200);
                  // «BAJA» es el id del botón de baja: `aplicarReglasDeCategoria` saca todo botón con
                  // ese id que no sea el suyo, así que este botón desaparecería en el próximo cambio.
                  onCambiar({ id: v === ID_BOTON_BAJA ? `${v}_2` : v });
                }}
                spellCheck={false}
                className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <p className="text-[11px] text-slate-400 mt-1">Mayúsculas, números y guion bajo. «{ID_BOTON_BAJA}» está reservado para la baja.</p>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default EditorBotones;
